import { EditFormData, UrlRule, ResponseRule, NestedRuleGroup, NestedResponseGroup } from './types';

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

const validateUrlRules = (rules: (UrlRule | NestedRuleGroup)[], prefix: string = 'urlRules'): ValidationError[] => {
  const errors: ValidationError[] = [];

  rules.forEach((rule, index) => {
    if ('rules' in rule) {
      if (rule.rules.length === 0) {
        errors.push({
          field: `${prefix}[${index}].rules`,
          message: `URL group ${index + 1}: Group must contain at least one rule`,
        });
      } else {
        const nestedErrors = validateUrlRules(rule.rules, `${prefix}[${index}].rules`);
        errors.push(...nestedErrors);
      }
    } else {
      if (!rule.type) {
        errors.push({
          field: `${prefix}[${index}].type`,
          message: `URL rule ${index + 1}: URL condition type is required`,
        });
      }

      if (!rule.operator) {
        errors.push({
          field: `${prefix}[${index}].operator`,
          message: `URL rule ${index + 1}: URL operator is required`,
        });
      }

      if (!rule.values || rule.values.length === 0) {
        errors.push({
          field: `${prefix}[${index}].values`,
          message: `URL rule ${index + 1}: URL values are required`,
        });
      } else {
        const emptyValues = rule.values.filter(value => !value || value.trim() === '');
        if (emptyValues.length > 0) {
          errors.push({
            field: `${prefix}[${index}].values`,
            message: `URL rule ${index + 1}: URL values cannot be empty`,
          });
        }
      }
    }
  });

  return errors;
};

const validateResponseRules = (rules: (ResponseRule | NestedResponseGroup)[], prefix: string = 'responseRules'): ValidationError[] => {
  const errors: ValidationError[] = [];

  rules.forEach((rule, index) => {
    if ('rules' in rule) {
      if (rule.rules.length === 0) {
        errors.push({
          field: `${prefix}[${index}].rules`,
          message: `Response group ${index + 1}: Group must contain at least one rule`,
        });
      } else {
        const nestedErrors = validateResponseRules(rule.rules, `${prefix}[${index}].rules`);
        errors.push(...nestedErrors);
      }
    } else {
      if (!rule.type) {
        errors.push({
          field: `${prefix}[${index}].type`,
          message: `Response rule ${index + 1}: Response condition type is required`,
        });
      }

      if (!rule.operator) {
        errors.push({
          field: `${prefix}[${index}].operator`,
          message: `Response rule ${index + 1}: Response operator is required`,
        });
      }

      if (!rule.value || rule.value.trim() === '') {
        errors.push({
          field: `${prefix}[${index}].value`,
          message: `Response rule ${index + 1}: Response value is required`,
        });
      } else {
        if (rule.type === 'duration' || rule.type === 'responseCode') {
          const numericValue = Number(rule.value);
          if (isNaN(numericValue)) {
            errors.push({
              field: `${prefix}[${index}].value`,
              message: `Response rule ${index + 1}: ${rule.type === 'duration' ? 'Duration' : 'Response Code'} must be a valid number`,
            });
          } else if (numericValue < 0) {
            errors.push({
              field: `${prefix}[${index}].value`,
              message: `Response rule ${index + 1}: ${rule.type === 'duration' ? 'Duration' : 'Response Code'} must be a positive number`,
            });
          }
        }
      }
    }
  });

  return errors;
};

export const validateFormData = (formData: EditFormData): ValidationResult => {
  const errors: ValidationError[] = [];

  if (!formData.allowBlock) {
    errors.push({
      field: 'allowBlock',
      message: 'Collect/Block selection is required',
    });
  }

  // Skip URL rules validation if "For All URL" is checked
  if (!formData.forAllUrl) {
    if (formData.urlRules.length === 0) {
      errors.push({
        field: 'urlRules',
        message: 'At least one URL rule is required',
      });
    } else {
      const urlRuleErrors = validateUrlRules(formData.urlRules);
      errors.push(...urlRuleErrors);
    }
  }

  // If "Applicable for all URLs" is selected, response rules are mandatory
  if (formData.forAllUrl) {
    if (formData.responseRules.length === 0) {
      errors.push({
        field: 'responseRules',
        message: 'At least one Response Code or Duration rule is required when "Applicable for all URLs" is selected',
      });
    } else {
      const responseRuleErrors = validateResponseRules(formData.responseRules);
      errors.push(...responseRuleErrors);
    }
  } else {
    // Response rules are optional when forAllUrl is not checked, but validate if present
    if (formData.responseRules.length > 0) {
      const responseRuleErrors = validateResponseRules(formData.responseRules);
      errors.push(...responseRuleErrors);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const formatValidationErrors = (errors: ValidationError[]): string => {
  return errors.map(error => error.message).join('\n');
};